import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, Download, Filter, RefreshCw, Receipt } from "lucide-react";
import { fmtDate, fmtEUR, monthsFR, statusLabel } from "@/lib/format";
import { toast } from "sonner";

export default function Loyers() {
  const [payments, setPayments] = useState([]);
  const [props, setProps] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [propFilter, setPropFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [payForm, setPayForm] = useState({ amount: 0, method: "virement", payment_date: new Date().toISOString().slice(0,10) });

  const load = async () => {
    const [p, pr, t] = await Promise.all([api.get("/payments"), api.get("/properties"), api.get("/tenants")]);
    setPayments(p.data); setProps(pr.data); setTenants(t.data);
  };
  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    await api.post("/payments/generate");
    toast.success("Échéances mises à jour");
    load();
  };

  const startPay = (p) => {
    setTarget(p);
    setPayForm({ amount: p.amount_due - (p.amount_paid || 0), method: "virement", payment_date: new Date().toISOString().slice(0,10) });
    setOpen(true);
  };
  const pay = async () => {
    try {
      await api.post(`/payments/${target.id}/pay`, { amount: +payForm.amount, method: payForm.method, payment_date: payForm.payment_date });
      toast.success("Paiement enregistré"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const downloadReceipt = async (p) => {
    try {
      const token = localStorage.getItem("gl_token");
      const resp = await fetch(`${API}/receipts/${p.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { const t = await resp.json().catch(() => ({detail: "Erreur"})); throw new Error(t.detail); }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `quittance-${p.period_year}-${String(p.period_month).padStart(2,'0')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Quittance téléchargée");
    } catch (e) { toast.error(e.message); }
  };

  const tenantName = (id) => { const t = tenants.find(x => x.id === id); return t ? `${t.first_name} ${t.last_name}` : "—"; };
  const propLabel = (id) => { const p = props.find(x => x.id === id); return p ? p.address : "—"; };

  const filtered = payments.filter(p => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (propFilter !== "all" && p.property_id !== propFilter) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="loyers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-overline">Encaissement</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:'Outfit'}}>Loyers & paiements</h1>
        </div>
        <Button variant="outline" onClick={regenerate} data-testid="regenerate-btn"><RefreshCw className="w-4 h-4 mr-1.5" />Mettre à jour échéances</Button>
      </div>

      <Card className="card-flat p-4 flex flex-wrap gap-3 items-end">
        <div className="flex items-center gap-1.5 text-zinc-600 text-sm"><Filter className="w-4 h-4" /> Filtres :</div>
        <div>
          <Label className="text-xs">Statut</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="paye">Payé</SelectItem>
              <SelectItem value="partiel">Partiel</SelectItem>
              <SelectItem value="retard">En retard</SelectItem>
              <SelectItem value="impaye">Impayé</SelectItem>
              <SelectItem value="a_venir">À venir</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Bien</Label>
          <Select value={propFilter} onValueChange={setPropFilter}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les biens</SelectItem>
              {props.map(p => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-3">Période</th><th className="px-4 py-3">Bien / Locataire</th>
              <th className="px-4 py-3">Échéance</th><th className="px-4 py-3 text-right">Dû</th>
              <th className="px-4 py-3 text-right">Payé</th><th className="px-4 py-3">Statut</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${p.status === "retard" || p.status === "impaye" ? "bg-red-50/40" : ""}`} data-testid={`payment-row-${p.id}`}>
                <td className="px-4 py-3 font-medium">{monthsFR[p.period_month]} {p.period_year}</td>
                <td className="px-4 py-3">
                  <div className="text-sm">{propLabel(p.property_id)}</div>
                  <div className="text-xs text-zinc-500">{tenantName(p.tenant_id)}</div>
                </td>
                <td className="px-4 py-3 text-xs">{fmtDate(p.due_date)}</td>
                <td className="px-4 py-3 mono text-right font-medium">{fmtEUR(p.amount_due)}</td>
                <td className="px-4 py-3 mono text-right">{fmtEUR(p.amount_paid || 0)}</td>
                <td className="px-4 py-3"><Badge className={`status-${p.status} border`} variant="outline">{statusLabel[p.status]}</Badge></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {p.status !== "paye" && <Button size="sm" variant="outline" onClick={() => startPay(p)} data-testid={`pay-btn-${p.id}`}><Wallet className="w-3.5 h-3.5 mr-1" />Encaisser</Button>}
                  {p.status === "paye" && <Button size="sm" variant="outline" onClick={() => downloadReceipt(p)} data-testid={`receipt-btn-${p.id}`}><Download className="w-3.5 h-3.5 mr-1" />Quittance</Button>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500"><Receipt className="w-8 h-8 mx-auto mb-2 opacity-40" />Aucune échéance.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Enregistrer un paiement</DialogTitle></DialogHeader>
          {target && (
            <div className="space-y-3">
              <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-sm">
                <div><strong>Période :</strong> {monthsFR[target.period_month]} {target.period_year}</div>
                <div><strong>Locataire :</strong> {tenantName(target.tenant_id)}</div>
                <div><strong>Dû :</strong> {fmtEUR(target.amount_due)} · <strong>Déjà payé :</strong> {fmtEUR(target.amount_paid || 0)}</div>
              </div>
              <div><Label>Montant à encaisser (€)</Label><Input data-testid="pay-amount" type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({...payForm, amount: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({...payForm, payment_date: e.target.value})} /></div>
                <div>
                  <Label>Mode</Label>
                  <Select value={payForm.method} onValueChange={(v) => setPayForm({...payForm, method: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="virement">Virement</SelectItem><SelectItem value="cheque">Chèque</SelectItem>
                      <SelectItem value="especes">Espèces</SelectItem><SelectItem value="prelevement">Prélèvement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={pay} data-testid="pay-confirm-btn">Valider</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
